import React from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Rating,
    Paper,
    CircularProgress,
    Alert,
} from "@mui/material";
import Carousel from "react-material-ui-carousel"; // Import Material-UI Carousel
import { useParams } from "react-router-dom";
import { useFetchAdDetailsQuery } from "../../store/services/adService";
import { IMAGE_URL } from "../../store/services/constants";
import colors from "../../styles/colors";
import Loader from "../../components/Loader";

const AdDetails = () => {
    const { id } = useParams(); // Get the ad ID from the URL params
    const { data, error, isLoading } = useFetchAdDetailsQuery(id); // Fetch ad details dynamically



    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Alert severity="error">Failed to load ad details!</Alert>
            </Box>
        );
    }

    const ad = data?.data || {};

    return (
        <Box sx={{ padding: 4, backgroundColor: "#f9f9f9" }}>
            {/* Header Section */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    marginBottom: 3,
                }}
            >
                <Avatar
                    src={`${IMAGE_URL}${ad.userimage}`}
                    alt={ad.username}
                    sx={{ width: 60, height: 60 }}
                />
                <Box>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily: "PBo", color: "rgba(247, 181, 0)" }}
                    >
                        {ad.name}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "PRe" }}>
                        Posted by {ad.username}
                    </Typography>
                </Box>
            </Box>

            {/* Split Layout with Slider and Details */}
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                {/* Image Slider Section */}
                <Box
                    sx={{
                        flex: "1",
                        maxWidth: "50%",
                    }}
                >
                    <Carousel
                        animation="slide"
                        autoPlay={true}
                        navButtonsAlwaysVisible
                        indicators={true}
                    >
                        {ad.images?.map((image) => (
                            <Box key={image.id}>
                                <img
                                    src={`${IMAGE_URL}${image.image}`}
                                    alt="Ad Image"
                                    style={{
                                        width: "100%",
                                        height: "300px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                    }}
                                />
                            </Box>
                        ))}
                    </Carousel>
                </Box>

                {/* Ad Details Section */}
                <Box sx={{ flex: "1" }}>
                    <Card sx={{ boxShadow: 4 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontFamily: "PSBo", mb: 2 }}>
                                Details
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>City:</strong> {ad.city}
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>Category:</strong> {ad.category?.name || "N/A"}
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>Pickup Location:</strong> {ad.pickup_location}
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>Weight:</strong> {ad.weight}
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>Price (Hourly):</strong> {ad.price_hourly} OMR
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>Features:</strong> {ad.features}
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', }} variant="body1">
                                    <strong>Rented Times:</strong> {ad.rented_times}
                                </Typography>
                                <Typography sx={{ fontFamily: 'PRe', display: 'flex', alignItems: 'center', }}>
                                    <strong style={{ marginRight: 2 }}>Rating:</strong> {ad.average_rating}{" "}
                                    <Rating
                                        value={parseFloat(ad.average_rating)}
                                        precision={0.5}
                                        sx={{ fontSize: 16, marginLeft: 1 }}
                                        readOnly
                                    />
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Description Section */}
            <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ fontFamily: "PBo", mb: 1 }}>
                    Description
                </Typography>
                <Paper elevation={3} sx={{ padding: 2 }}>
                    <Typography variant="body1" sx={{ fontFamily: "PRe" }}>
                        {ad.description}
                    </Typography>
                </Paper>
            </Box>

            {/* Ratings Section */}
            <Box sx={{ mt: 3 }}>
                <Typography
                    variant="h6"
                    sx={{ fontFamily: "PBo", color: "rgba(247, 181, 0)", mb: 2 }}
                >
                    Ratings & Reviews
                </Typography>
                <Box>
                    {ad.ratings?.length > 0 ? (
                        ad.ratings.map((rating) => (
                            <Paper
                                key={rating.id}
                                elevation={2}
                                sx={{
                                    padding: 2,
                                    marginBottom: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                }}
                            >
                                <Avatar
                                    src={`${IMAGE_URL}${rating.user.image}`}
                                    alt={rating.user.username}
                                    sx={{ width: 40, height: 40 }}
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontFamily: "PBo" }}>
                                        {rating.user.username}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: "PRe", color: "gray" }}
                                    >
                                        {rating.rating_text}
                                    </Typography>
                                    <Rating
                                        value={rating.ratings}
                                        precision={0.5}
                                        readOnly
                                        sx={{ mt: 0.5 }}
                                    />
                                </Box>
                            </Paper>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ color: "gray" }}>
                            No ratings available.
                        </Typography>
                    )}
                </Box>
            </Box>
            <Loader open={isLoading} />
        </Box>
    );
};

export default AdDetails;


// import React from "react";
// import {
//   Box,
//   Typography,
//   Card,
//   CardMedia,
//   CardContent,
//   Avatar,
//   Chip,
//   Rating,
//   Grid,
//   Paper,
//   Divider,
//   CircularProgress,
//   Alert,
// } from "@mui/material";
// import { useParams } from "react-router-dom";
// import { useFetchAdDetailsQuery } from "../../store/services/adService";
// import { IMAGE_URL } from "../../store/services/constants";

// const AdDetails = () => {
//   const { id } = useParams(); // Get the ad ID from the URL params
//   const { data, error, isLoading } = useFetchAdDetailsQuery(id); // Fetch ad details dynamically

//   if (isLoading) {
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (error) {
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//         }}
//       >
//         <Alert severity="error">Failed to load ad details!</Alert>
//       </Box>
//     );
//   }

//   const ad = data?.data || {};

//   return (
//     <Box sx={{ padding: 4, backgroundColor: "#f9f9f9" }}>
//       {/* Header Section */}
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           gap: 2,
//           marginBottom: 3,
//         }}
//       >
//         <Avatar
//           src={`${IMAGE_URL}${ad.userimage}`}
//           alt={ad.username}
//           sx={{ width: 60, height: 60 }}
//         />
//         <Box>
//           <Typography
//             variant="h5"
//             sx={{ fontFamily: "PBo", color: "rgba(247, 181, 0)" }}
//           >
//             {ad.name}
//           </Typography>
//           <Typography variant="body1" sx={{ fontFamily: "PRe" }}>
//             Posted by {ad.username}
//           </Typography>
//         </Box>
//       </Box>

//       {/* Main Content Section */}
//       <Card sx={{ display: "flex", mb: 3, boxShadow: 4 }}>
//         <CardMedia
//           component="img"
//           height="300"
//           image={`${IMAGE_URL}${ad.images?.[0]?.image}`}
//           alt={ad.name}
//           sx={{ flex: 1 }}
//         />
//         <CardContent sx={{ flex: 1 }}>
//           <Typography variant="h6" sx={{ fontFamily: "PBo", mb: 2 }}>
//             Details
//           </Typography>
//           <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
//             <Typography variant="body1">
//               <strong>City:</strong> {ad.city}
//             </Typography>
//             <Typography variant="body1">
//               <strong>Category:</strong> {ad.category?.name || "N/A"}
//             </Typography>
//             <Typography variant="body1">
//               <strong>Pickup Location:</strong> {ad.pickup_location}
//             </Typography>
//             <Typography variant="body1">
//               <strong>Weight:</strong> {ad.weight}
//             </Typography>
//             <Typography variant="body1">
//               <strong>Price (Hourly):</strong> {ad.price_hourly} OMR
//             </Typography>
//             <Typography variant="body1">
//               <strong>Features:</strong> {ad.features}
//             </Typography>
//             <Typography variant="body1">
//               <strong>Rented Times:</strong> {ad.rented_times}
//             </Typography>
//             <Typography variant="body1">
//               <strong>Rating:</strong> {ad.average_rating}{" "}
//               <Rating
//                 value={parseFloat(ad.average_rating)}
//                 precision={0.5}
//                 readOnly
//               />
//             </Typography>
//           </Box>
//         </CardContent>
//       </Card>

//       {/* Image Gallery Section */}
//       <Box sx={{ marginBottom: 3 }}>
//         <Typography
//           variant="h6"
//           sx={{ fontFamily: "PBo", color: "rgba(247, 181, 0)", mb: 2 }}
//         >
//           Gallery
//         </Typography>
//         <Grid container spacing={2}>
//           {ad.images?.map((image) => (
//             <Grid item xs={12} sm={6} md={4} key={image.id}>
//               <Paper elevation={3}>
//                 <CardMedia
//                   component="img"
//                   height="200"
//                   image={`${IMAGE_URL}${image.image}`}
//                   alt="Ad Image"
//                   sx={{ borderRadius: 1 }}
//                 />
//               </Paper>
//             </Grid>
//           ))}
//         </Grid>
//       </Box>

//       {/* Description Section */}
//       <Box sx={{ mb: 3 }}>
//         <Typography variant="h6" sx={{ fontFamily: "PBo", mb: 1 }}>
//           Description
//         </Typography>
//         <Paper elevation={3} sx={{ padding: 2 }}>
//           <Typography variant="body1" sx={{ fontFamily: "PRe" }}>
//             {ad.description}
//           </Typography>
//         </Paper>
//       </Box>

//       {/* Ratings Section */}
//       <Box>
//         <Typography
//           variant="h6"
//           sx={{ fontFamily: "PBo", color: "rgba(247, 181, 0)", mb: 2 }}
//         >
//           Ratings & Reviews
//         </Typography>
//         <Box>
//           {ad.ratings?.length > 0 ? (
//             ad.ratings.map((rating) => (
//               <Paper
//                 key={rating.id}
//                 elevation={2}
//                 sx={{
//                   padding: 2,
//                   marginBottom: 2,
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 2,
//                 }}
//               >
//                 <Avatar
//                   src={`${IMAGE_URL}${rating.user.image}`}
//                   alt={rating.user.username}
//                   sx={{ width: 40, height: 40 }}
//                 />
//                 <Box>
//                   <Typography variant="body1" sx={{ fontFamily: "PBo" }}>
//                     {rating.user.username}
//                   </Typography>
//                   <Typography
//                     variant="body2"
//                     sx={{ fontFamily: "PRe", color: "gray" }}
//                   >
//                     {rating.rating_text}
//                   </Typography>
//                   <Rating
//                     value={rating.ratings}
//                     precision={0.5}
//                     readOnly
//                     sx={{ mt: 0.5 }}
//                   />
//                 </Box>
//               </Paper>
//             ))
//           ) : (
//             <Typography variant="body2" sx={{ color: "gray" }}>
//               No ratings available.
//             </Typography>
//           )}
//         </Box>
//       </Box>
//     </Box>
//   );
// };

// export default AdDetails;